import { cloneElement, useRef, useState } from "react";
import type { ReactElement, ReactNode, Ref } from "react";
import Tooltip from "@mui/material/Tooltip";

interface OverflowTooltipProps {
  children: ReactElement<{ ref?: Ref<HTMLElement> }>;
  title: ReactNode;
}

/**
 * Shows `title` on hover only when the child's text is truncated (measured at hover time). The
 * child must be the element that truncates (e.g. a noWrap Typography) and must accept a ref.
 */
export const OverflowTooltip = (props: OverflowTooltipProps) => {
  const { children, title } = props;
  const [isOpen, setIsOpen] = useState(false);
  const elementRef = useRef<HTMLElement>(null);
  const child = cloneElement(children, { ref: elementRef });

  const handleOpen = () => {
    const element = elementRef.current;
    if (element && element.scrollWidth > element.clientWidth) {
      setIsOpen(true);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  return (
    <Tooltip
      onClose={handleClose}
      onOpen={handleOpen}
      open={isOpen}
      placement="top-start"
      title={title}
    >
      {child}
    </Tooltip>
  );
};
